import type { FieldErrors, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import { timezoneNames } from '@/utils/date/timezoneNames';

import type { LocationFormValues } from './locationFormValues';

interface TimezoneFieldProps {
    register: UseFormRegister<LocationFormValues>;
    errors: FieldErrors<LocationFormValues>;
    /** The edited location's timezone as stored, which may be an alias of the one selected. */
    storedTimezone?: string;
    /** The zone the form starts on: the canonical name of `storedTimezone`. */
    initialTimezone: string;
}

// Only canonical zones are offered: when this dropdown listed `Intl`-era aliases as well,
// `Iceland`, `Canada/Newfoundland` and friends ended up stored on 81 locations, and an alias
// stops resolving whenever the database's tzdata drops the backward links.

/** The location's timezone select. */
export default function TimezoneField(props: TimezoneFieldProps) {
    const { register, errors, storedTimezone, initialTimezone } = props;
    const { t } = useTranslation('clientLocations');
    // A stored name nothing resolves stays visible as it is; the API refuses it on save.
    const zones = timezoneNames();
    const options = zones.includes(initialTimezone) ? zones : [initialTimezone, ...zones];
    const storedAsAlias =
        storedTimezone !== undefined && storedTimezone !== initialTimezone
            ? t('addLocationModal.timezoneCanonical', {
                  stored: storedTimezone,
                  timezone: initialTimezone,
              })
            : undefined;

    return (
        <FormField
            label={t('addLocationModal.timezone')}
            htmlFor="timezone"
            hint={storedAsAlias}
            error={errors.timezone ? t('addLocationModal.timezoneRequired') : undefined}>
            <Select
                {...register('timezone', {
                    required: true,
                    minLength: 1,
                })}
                autoComplete="off">
                {options.map((tz) => (
                    <option key={tz} value={tz}>
                        {tz}
                    </option>
                ))}
            </Select>
        </FormField>
    );
}
